/**
 * Time on task for the pilot study. Minutes come from the answers themselves: each answer's time, capped at two
 * minutes so a child who walks away from the screen doesn't count as practicing. Weeks start Monday.
 */
import type { ActivityRow } from "../data/store";

export const CAP_MS = 120_000;

/** Monday 00:00 (local) of the week the date falls in, as YYYY-MM-DD. */
export function weekOf(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
export const thisWeek = (now = new Date()): string => weekOf(now);
/** The week's start as an ISO instant, for querying "since Monday". */
export function weekStartISO(now = new Date()): string {
  const [y, m, d] = thisWeek(now).split("-").map(Number);
  return new Date(y!, m! - 1, d!).toISOString();
}

export const minutesOf = (rows: ActivityRow[]): number => rows.reduce((s, r) => s + Math.min(Math.max(r.ms, 0), CAP_MS), 0) / 60000;

/** Minutes per week, oldest first. */
export function minutesByWeek(rows: ActivityRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) { const w = weekOf(new Date(r.at)); m.set(w, (m.get(w) ?? 0) + Math.min(Math.max(r.ms, 0), CAP_MS) / 60000); }
  return new Map([...m.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

/** Whole weeks from the joining week through this week (at least 1). */
export function weeksSince(joinedISO: string, now = new Date()): number {
  const a = new Date(weekOf(new Date(joinedISO))), b = new Date(weekOf(now));
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / (7 * 864e5)) + 1);
}

/** A CSV cell: quoted when it needs to be. */
export const csvCell = (v: unknown): string => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
export const csv = (rows: unknown[][]): string => rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
